import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useState } from "react";
import useWorkoutPages from "./useWorkoutPages";
import useWorkoutPageDisplayStrings from "./useWorkoutPageDisplayStrings";
import { useSnackbarContext, useUserContext } from "@WahooFitness/wahoo-offline-mfe";
import {
  ImperialMetricDisplayPreference,
  WahooWorkoutTypeLocation,
} from "@WahooFitness/cloud-client-types";
import { MenuListItemVariant } from "@WahooFitness/redesignr";
import { ChevronRight } from "@carbon/icons-react";
import { Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { FuzzyResult } from "@nozbe/microfuzz";
import { useFuzzySearchList } from "@nozbe/microfuzz/react";
import useWorkoutPageCategoryStrings from "./useWorkoutPageCategoryStrings";
import useWorkoutPageFieldStrings from "./useWorkoutPageFieldStrings";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import {
  BoltDisplayCategory,
  BoltDisplayField,
  crux_bolt_display_page_type_e,
  crux_bolt_display_pedaling_monitor_graph_type_e,
  crux_defn_type_e,
} from "@WahooFitness/crux-js-display-cfg";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { t } from "@lingui/macro";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { getCruxAppProfile } from "@/services/AppProfileUtil";
import { PLAYR_DEFAULT_ID, playrFieldMaxDefaults } from "./playrFieldMaxDefaults";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";

const CORE_HEATZONE_FIELDS = [
  crux_defn_type_e.CORE_HSI,
  crux_defn_type_e.CORE_HSI_COLORED,
  crux_defn_type_e.CORE_HEAT_ZONE,
  crux_defn_type_e.CORE_HEAT_ZONE_HSI,
  crux_defn_type_e.TIZ_CORE_HEAT_1,
  crux_defn_type_e.TIZ_CORE_HEAT_2,
  crux_defn_type_e.TIZ_CORE_HEAT_3,
  crux_defn_type_e.TIZ_CORE_HEAT_4,
];

const FLOW_BIO_FIELDS = [crux_defn_type_e.FLUID_LOSS, crux_defn_type_e.SODIUM_LOSS];

const VITAL_PRO_FIELDS = [
  crux_defn_type_e.BREATHING_RATE,
  crux_defn_type_e.MINUTE_VENTILATION,
  crux_defn_type_e.MINUTE_VENTILATION_5S,
  crux_defn_type_e.MINUTE_VENTILATION_60S,
  crux_defn_type_e.TIDAL_VOLUME,
];

const VITAL_PRO_ZONES = [
  crux_defn_type_e.MINUTE_VENTILATION_5S_COLORED,
  crux_defn_type_e.MINUTE_VENTILATION_60S_COLORED,
  crux_defn_type_e.VENTILATION_ZONE,
  crux_defn_type_e.VENTILATION_ZONE_COLORED,
  crux_defn_type_e.VENTILATION_TIZ_VISUAL,
  crux_defn_type_e.TIZ_VENTILATION_1,
  crux_defn_type_e.TIZ_VENTILATION_2,
  crux_defn_type_e.TIZ_VENTILATION_3,
  crux_defn_type_e.TIZ_VENTILATION_4,
  crux_defn_type_e.TIZ_VENTILATION_5,
];

const ELEMNT_ONLY_FIELDS = [
  crux_defn_type_e.ELEVATION,
  crux_defn_type_e.ELEVATION_MAX,
  crux_defn_type_e.ELEVATION_MIN,
  ...CORE_HEATZONE_FIELDS,
  ...FLOW_BIO_FIELDS,
  ...VITAL_PRO_FIELDS,
  ...VITAL_PRO_ZONES,
];

function getCategoryFields(
  category: BoltDisplayCategory | undefined,
  includeSubCategories?: boolean
) {
  if (!category) {
    return [];
  }
  return [
    ...category.getFields(),
    ...(includeSubCategories ? category.getSubCategories().flatMap((c) => c.getFields()) : []),
  ];
}

const useEditWorkoutPage = (profile?: WSMWorkoutProfile, pageId?: number, elevation?: boolean) => {
  const {
    getPage,
    pagesAreLoading,
    getCategoriesForPageType,
    savePages,
    flushPendingSave,
    displayConfig,
    setDisplayConfig,
    elevationPage,
    maxPageNameLength,
  } = useWorkoutPages(elevation);
  const { enabled: showVitalProZones } = useSettingsVersionedFeatures(
    "SHOW_VITAL_PRO_ZONES_MINPROFILE_VERSION"
  );

  const { enqueueSnackbar } = useSnackbarContext();

  const page = useMemo(
    () => (elevation ? elevationPage?.getClimbSubPage() : getPage(pageId || 0)),
    [elevation, elevationPage, getPage, pageId]
  );

  const {
    associatedDevicesIsLoading,
    associatedDevicesError,
    associatedBoltTypes,
    hasAssociatedElemnt,
  } = useAssociatedDevices();

  const maxFieldDevices = useMemo((): Record<number, Set<number>> => {
    if (!page) return {};
    if (!associatedBoltTypes || associatedBoltTypes.length === 0) {
      const pageType = page.getType?.();
      const max = playrFieldMaxDefaults[pageType];

      if (max !== undefined) {
        return {
          [max]: new Set([PLAYR_DEFAULT_ID]),
        };
      }
    }

    return associatedBoltTypes.reduce(
      (acc, type) => {
        const maxFields = page.getMaxFields(type);
        if (acc[maxFields] === undefined) {
          acc[maxFields] = new Set();
        }
        acc[maxFields].add(type);
        return acc;
      },
      {} as Record<number, Set<number>>
    );
  }, [associatedBoltTypes, page]);

  const [minFields, maxFields] = useMemo(() => {
    const maxes = Object.keys(maxFieldDevices).map(Number);
    if (maxes.length === 0) return [0, 0];
    return [Math.min(...maxes), Math.max(...maxes)];
  }, [maxFieldDevices]);
  const fieldsCount = useMemo(() => page?.getFieldsCount() || 0, [page]);

  const { workoutPageTranslations } = useWorkoutPageDisplayStrings();

  const isMapPage = useMemo(() => !!page?.isMapPage(), [page]);
  const isIndoorProfile = useMemo(
    () => profile?.workout_type_location_id === WahooWorkoutTypeLocation.Indoor,
    [profile?.workout_type_location_id]
  );

  const pageNameOverride = useMemo(() => {
    if (isMapPage && isIndoorProfile) {
      return t`Route`;
    }
    return undefined;
  }, [isIndoorProfile, isMapPage]);

  const pageName = useMemo(
    () =>
      page &&
      (page?.getCustomTitle() || pageNameOverride || workoutPageTranslations[page.getType()]),
    [page, pageNameOverride, workoutPageTranslations]
  );

  const isPedalMonitorPage = useMemo(() => !!page?.isPedalMonitorPage(), [page]);

  const isPlannedWorkoutPage = useMemo(
    () => page?.getType() === crux_bolt_display_page_type_e.BOLTAPP_PLANNED_WORKOUT,
    [page]
  );

  const [pageNameInputValue, setPageNameInputValue] = useState<string>("");
  const [pageNameError, setPageNameError] = useState("");

  useEffect(() => {
    setPageNameInputValue(pageName || "");
  }, [pageName]);

  const handlePageNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value.length > maxPageNameLength) {
        setPageNameError(t`Please enter a value with ${maxPageNameLength} or less characters`);
      } else {
        setPageNameError("");
      }
      setPageNameInputValue(value);
    },
    [maxPageNameLength]
  );

  const handlePageNameBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (!page || !displayConfig || value.length > maxPageNameLength) {
        return;
      }
      page?.setCustomTitle(value);
      const updatedPage = page.updateParentPage();
      displayConfig?.update(updatedPage);
      savePages(true);
    },
    [displayConfig, maxPageNameLength, page, savePages]
  );

  const [displayFields, setDisplayFields] = useState<BoltDisplayField[]>([]);

  useEffect(() => {
    if (!page) {
      return;
    }

    const fields = [];
    for (let i = 0; i < fieldsCount; i++) {
      const field = page.getFieldAt(i);

      const isElemntOnly = ELEMNT_ONLY_FIELDS.includes(field.getId()) && !hasAssociatedElemnt;
      if (!isElemntOnly) {
        fields.push(field);
      }
    }
    setDisplayFields(fields);
  }, [fieldsCount, page, hasAssociatedElemnt]);

  const cruxPermanentDisplayFields = useMemo(
    () => displayFields.filter((field) => field.isPermanent()),
    [displayFields]
  );

  const [fieldToReplace, setFieldToReplace] = useState<BoltDisplayField | undefined>(undefined);
  const [selectedCategories, setSelectedCategories] = useState<BoltDisplayCategory[]>([]);
  const lastSelectedCategory = useMemo(
    () =>
      selectedCategories.length > 0 ? selectedCategories[selectedCategories.length - 1] : undefined,
    [selectedCategories]
  );
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [changingPedalMonitorGraphType, setChangingPedalMonitorGraphType] = useState(false);

  const openCategoryDialog = useCallback(
    (fieldToReplace?: BoltDisplayField, pedalMonitorGraphType?: boolean) => {
      setFieldToReplace(fieldToReplace);
      setCategoryDialogOpen(true);
      setSearchTerm("");
      setChangingPedalMonitorGraphType(pedalMonitorGraphType || false);
    },
    [setFieldToReplace]
  );

  const closeCategoryDialog = useCallback(() => {
    setFieldToReplace(undefined);
    setSelectedCategories([]);
    setCategoryDialogOpen(false);
    setSearchTerm("");
    setChangingPedalMonitorGraphType(false);
  }, [setFieldToReplace, setSelectedCategories]);

  const handleChangeSearchTerm = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const { workoutFieldTranslations, pedalMonitorGraphTypeFieldTranslations } =
    useWorkoutPageFieldStrings();
  const { workoutPageCategoryTranslations } = useWorkoutPageCategoryStrings();

  const baseCategories = useMemo(
    () =>
      getCategoriesForPageType(page?.getType()).sort((left, right) =>
        workoutPageCategoryTranslations[left.getId()].localeCompare(
          workoutPageCategoryTranslations[right.getId()]
        )
      ),
    [getCategoriesForPageType, page, workoutPageCategoryTranslations]
  );

  const getFieldTranslation = useCallback(
    (field: BoltDisplayField) => {
      const translation = workoutFieldTranslations[field.getId()];
      if (!translation) {
        console.warn(
          `No translation found for field ${field.getId()} (${field.getName(field.getId())})`
        );
      }
      return translation || `NEED I18N ${field.getId()} ${field.getName(field.getId())}`;
    },
    [workoutFieldTranslations]
  );

  const allAvailableFields = useMemo(() => {
    return baseCategories
      .reduce((acc, category) => {
        return [...acc, ...getCategoryFields(category, true)];
      }, [] as BoltDisplayField[])
      .filter((field) => showVitalProZones || !VITAL_PRO_ZONES.includes(field.getId()))
      .sort((left, right) => getFieldTranslation(left).localeCompare(getFieldTranslation(right)));
  }, [baseCategories, getFieldTranslation, showVitalProZones]);

  const getText = useCallback(
    (field: BoltDisplayField) => [getFieldTranslation(field)],
    [getFieldTranslation]
  );

  const mapResultItem = useCallback((val: FuzzyResult<BoltDisplayField>) => val.item, []);

  const matchingFields = useFuzzySearchList({
    list: allAvailableFields,
    queryText: searchTerm,
    getText,
    mapResultItem,
  });

  const availableCategories = useMemo(() => {
    return lastSelectedCategory
      ? lastSelectedCategory
          .getSubCategories()
          .sort((left, right) =>
            workoutPageCategoryTranslations[left.getId()].localeCompare(
              workoutPageCategoryTranslations[right.getId()]
            )
          )
      : baseCategories;
  }, [baseCategories, lastSelectedCategory, workoutPageCategoryTranslations]);

  const availableFields = useMemo(() => {
    return searchTerm
      ? matchingFields
      : getCategoryFields(lastSelectedCategory)
          .filter((field) => showVitalProZones || !VITAL_PRO_ZONES.includes(field.getId()))
          .sort((left, right) =>
            getFieldTranslation(left).localeCompare(getFieldTranslation(right))
          ) || [];
  }, [getFieldTranslation, lastSelectedCategory, matchingFields, searchTerm, showVitalProZones]);

  const replaceField = useCallback(
    (newField: BoltDisplayField) => {
      if (!fieldToReplace || !page) {
        return;
      }
      const replaceIndex = page.getIndexOfField(fieldToReplace);
      page.removeFieldAt(replaceIndex);
      page.insertFieldAt(replaceIndex, newField);
      const updatedPage = page.updateParentPage();
      const newDisplayConfig = displayConfig?.update(updatedPage);
      setDisplayConfig(newDisplayConfig);
      savePages();
    },
    [displayConfig, fieldToReplace, page, savePages, setDisplayConfig]
  );

  const addField = useCallback(
    (newField: BoltDisplayField) => {
      if (!page) return;
      page?.addField(newField);
      const updatedPage = page.updateParentPage();
      const newDisplayConfig = displayConfig?.update(updatedPage);
      setDisplayConfig(newDisplayConfig);
      savePages();
      closeCategoryDialog();
      enqueueSnackbar({
        message: t`"${getFieldTranslation(newField)}" added`,
        enableClickAway: true,
      });
    },
    [
      page,
      displayConfig,
      setDisplayConfig,
      savePages,
      closeCategoryDialog,
      enqueueSnackbar,
      getFieldTranslation,
    ]
  );

  const removeField = useCallback(
    (field: BoltDisplayField) => {
      if (!page) return;
      const fieldIndex = page.getIndexOfField(field);
      page?.removeFieldAt(fieldIndex);
      const updatedPage = page.updateParentPage();
      const newDisplayConfig = displayConfig?.update(updatedPage);
      setDisplayConfig(newDisplayConfig);
      savePages();
    },
    [displayConfig, page, savePages, setDisplayConfig]
  );

  const handleFieldOptionClick = useCallback(
    (clickedField: BoltDisplayField) => {
      if (!page) return;
      if (fieldToReplace) {
        replaceField(clickedField);
        closeCategoryDialog();
        return;
      }
      if (page.containsField(clickedField)) {
        removeField(clickedField);
        return;
      }
      if (fieldsCount < maxFields) {
        addField(clickedField);
      }
    },
    [
      addField,
      closeCategoryDialog,
      fieldToReplace,
      fieldsCount,
      page,
      removeField,
      replaceField,
      maxFields,
    ]
  );

  const moveField = useCallback(
    (startIndex: number, newIndex: number) => {
      if (!page || startIndex === newIndex) return;

      const field = page.getFieldAt(startIndex);
      if (!field) return;

      setDisplayFields((currentDisplayFields) => {
        const newDisplayFields = [...currentDisplayFields];
        const [movedField] = newDisplayFields.splice(startIndex, 1);
        newDisplayFields.splice(newIndex, 0, movedField);
        return newDisplayFields;
      });

      page.removeFieldAt(startIndex);
      page.insertFieldAt(newIndex, field);

      const updatedPage = page.updateParentPage();
      const newDisplayConfig = displayConfig?.update(updatedPage);
      setDisplayConfig(newDisplayConfig);
      savePages();
    },
    [displayConfig, page, savePages, setDisplayConfig]
  );

  const theme = useTheme();
  const { currentUser } = useUserContext();

  const handlePageReset = useCallback(() => {
    if (!page || !displayConfig || !profile) return;
    page.resetToDefault(profile.workout_type_id || 0, getCruxAppProfile(currentUser?.app?.profile));
    const updatedPage = page.updateParentPage();
    const newDisplayConfig = displayConfig?.update(updatedPage);
    setDisplayConfig(newDisplayConfig);
    savePages(true);
  }, [currentUser?.app?.profile, displayConfig, page, profile, savePages, setDisplayConfig]);

  const fieldIdsOnPage = useMemo(
    () => displayFields.map((field) => field.getId()),
    [displayFields]
  );

  const savePage = useCallback(
    async (immediately?: boolean) => {
      if (!page) return;
      const updatedPage = page.updateParentPage();
      displayConfig?.update(updatedPage);
      await savePages(immediately);
    },
    [displayConfig, page, savePages]
  );

  const dialogMenuItems = useMemo(() => {
    if (!profile || !page || !currentUser) return [];
    return availableFields.length > 0 || searchTerm
      ? availableFields?.map((field) => {
          const fieldPosition = page.getIndexOfField(field) + 1;
          return {
            key: field.getId(),
            variant: MenuListItemVariant.Action,
            id: field.getId().toString(),
            action: () => handleFieldOptionClick(field),
            actionComponent: (
              <Typography variant="prose-base" color="text.secondary">
                {field.getUnitName(
                  profile.workout_type_id || 0,
                  currentUser.display_preference?.speed_distance !==
                    ImperialMetricDisplayPreference.imperial,
                  currentUser.display_preference?.elevation !==
                    ImperialMetricDisplayPreference.imperial,
                  currentUser.display_preference?.weight !==
                    ImperialMetricDisplayPreference.imperial,
                  currentUser.display_preference?.temperature !==
                    ImperialMetricDisplayPreference.imperial
                )}
              </Typography>
            ),
            content: getFieldTranslation(field),
            icon:
              !fieldToReplace && fieldPosition > 0 ? (
                <Box
                  bgcolor="success.main"
                  borderRadius="50%"
                  height={20}
                  width={20}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography color="success.contrastText" variant="ui-2xs-medium">
                    {fieldPosition}
                  </Typography>
                </Box>
              ) : undefined,
            disabled:
              (fieldToReplace && fieldIdsOnPage.includes(field.getId())) ||
              (!fieldToReplace &&
                fieldsCount >= maxFields &&
                !fieldIdsOnPage.includes(field.getId())),
          };
        })
      : availableCategories.map((category) => ({
          key: category.getId(),
          variant: MenuListItemVariant.Action,
          id: category.getId().toString(),
          content: workoutPageCategoryTranslations[category.getId()],
          action: () => {
            setSelectedCategories((current) => [...current, category]);
          },
          actionComponent: (
            <ChevronRight height={24} width={24} color={theme.palette.text.secondary} />
          ),
        }));
  }, [
    availableCategories,
    availableFields,
    currentUser,
    fieldIdsOnPage,
    fieldToReplace,
    fieldsCount,
    getFieldTranslation,
    handleFieldOptionClick,
    maxFields,
    page,
    profile,
    searchTerm,
    theme.palette.text.secondary,
    workoutPageCategoryTranslations,
  ]);

  const [showElevationProfile, setShowElevationProfile] = useState(true);

  useEffect(() => {
    setShowElevationProfile(page?.getMapShowElevationProfile() || false);
  }, [page]);

  const toggleShowElevationProfile = useCallback(() => {
    page?.setMapShowElevationProfile(!showElevationProfile);
    setShowElevationProfile((prev) => !prev);
    savePage();
  }, [page, savePage, showElevationProfile]);

  const [pedalMonitorGraphType, setPedalMonitorGraphType] = useState(0);

  useEffect(() => {
    setPedalMonitorGraphType(page?.getPedalingMonitorGraphType() || 0);
  }, [page]);

  const handleChangePedalMonitorGraphType = useCallback(
    (graphType: number) => {
      page?.setPedalingMonitorGraphType(pedalMonitorGraphType);
      setPedalMonitorGraphType(graphType);
      savePage();
      closeCategoryDialog();
    },
    [closeCategoryDialog, page, pedalMonitorGraphType, savePage]
  );

  const pedalMonitorGraphTypeMenuItems = useMemo(() => {
    const fieldOptions = [
      crux_bolt_display_pedaling_monitor_graph_type_e.FORCE_VECTORS_POWER_LR,
      crux_bolt_display_pedaling_monitor_graph_type_e.FORCE_VECTORS_BALANCE_LR,
      crux_bolt_display_pedaling_monitor_graph_type_e.FORCE_VECTORS_EFFICIENCY_LR,
      crux_bolt_display_pedaling_monitor_graph_type_e.TORQUE_CHART_POWER_LR,
      crux_bolt_display_pedaling_monitor_graph_type_e.TORQUE_CHART_BALANCE_LR,
      crux_bolt_display_pedaling_monitor_graph_type_e.TORQUE_CHART_EFFICIENCY_LR,
    ];
    return fieldOptions.map((fieldOption) => ({
      key: fieldOption.toString(),
      variant: MenuListItemVariant.Action,
      id: fieldOption.toString(),
      content: pedalMonitorGraphTypeFieldTranslations[fieldOption],
      action: () => handleChangePedalMonitorGraphType(fieldOption),
    }));
  }, [handleChangePedalMonitorGraphType, pedalMonitorGraphTypeFieldTranslations]);

  const { disablePubSubUpdates, enablePubSubUpdates } = useWorkoutProfilesContext();

  useEffect(() => {
    disablePubSubUpdates();
    return enablePubSubUpdates;
  }, [disablePubSubUpdates, enablePubSubUpdates]);

  return {
    pagesAreLoading: pagesAreLoading || associatedDevicesIsLoading,
    error: associatedDevicesError,
    pageName,
    pageNameError,
    maxFields,
    minFields,
    maxFieldDevices,
    displayFields,
    cruxPermanentDisplayFields,
    fieldsCount,
    openCategoryDialog,
    closeCategoryDialog,
    changingPedalMonitorGraphType,
    removeField,
    categoryDialogOpen,
    setFieldToReplace,
    selectedCategories,
    setSelectedCategories,
    lastSelectedCategory,
    searchTerm,
    handleChangeSearchTerm,
    dialogMenuItems,
    pedalMonitorGraphTypeMenuItems,
    pageNameInputValue,
    handlePageNameChange,
    handlePageNameBlur,
    moveField,
    savePage,
    flushPendingSave,
    isMapPage,
    isPedalMonitorPage,
    showElevationProfile,
    toggleShowElevationProfile,
    pedalMonitorGraphType,
    handleChangePedalMonitorGraphType,
    handlePageReset,
    page,
    isPlannedWorkoutPage,
  };
};

export default useEditWorkoutPage;
